import React from 'react';

import { DashedTag, TabOption } from '@/components/chariot-ui';
import { Link } from '@/components/Link';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { LoginPanelRequestRow as LoginPanelRequestCell } from '@/sections/poe/components/LoginPanelRequestRow';
import type {
  MainTabKey,
  POEContent,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { Webpage } from '@/types';
import { formatDate } from '@/utils/date.util';
import { isValidExternalURL } from '@/utils/url.util';

export function addLoginPanelTab({
  sections,
  tempTabs,
  webpage,
  poeData,
  mainTabKeys,
}: {
  sections: Record<string, SectionContent>;
  tempTabs: Record<MainTabKey, TabOption[]>;
  webpage: Webpage;
  poeData?: POEContent;
  mainTabKeys: MainTabKey[];
}) {
  if (!mainTabKeys.includes('LOGIN_PANEL')) return;

  const screenshot = webpage.screenshot;

  const proofInputFields = Array.isArray(poeData?.input_fields)
    ? poeData.input_fields
    : [];
  const allInputFields = Array.from(new Set(proofInputFields.filter(Boolean)));

  const foundAtURL =
    poeData?.found_at_url || poeData?.['matched-at'] || webpage.url;
  const resolvedTo = poeData?.resolved_to;
  const matchedAtURL = poeData?.matched_at;
  const detectorName = poeData?.detector_name;
  const snippet = poeData?.snippet;
  const crawledAt = poeData?.crawled_at || webpage.created;
  const sourceURL = poeData?.source_url || webpage.url;
  const method = poeData?.method || 'GET';

  const contentLength =
    typeof poeData?.content_length === 'number'
      ? poeData.content_length.toString()
      : webpage.metadata?.content_length?.toString() ||
        (snippet ? new Blob([snippet]).size.toString() : '');

  tempTabs['LOGIN_PANEL'].push({
    label: 'Request',
    value: 'login_panel_request',
  });
  sections['login_panel_request'] = (
    <div className="text-chariot-text-secondary grid grid-cols-2 gap-2 text-sm">
      <div className="col-span-2 flex items-center gap-2">
        <span>Request URL:</span>
        {isValidExternalURL(foundAtURL) ? (
          <Link
            to={foundAtURL}
            target="_blank"
            rel="noopener noreferrer"
            buttonClass="text-left p-0"
          >
            <span>{foundAtURL}</span>
          </Link>
        ) : (
          <span className="text-chariot-text-primary text-sm break-all">
            {foundAtURL}
          </span>
        )}
      </div>
      {resolvedTo && resolvedTo !== foundAtURL && (
        <div className="col-span-2 flex items-center gap-2">
          <span>Resolved To:</span>
          {isValidExternalURL(resolvedTo) ? (
            <Link
              to={resolvedTo}
              target="_blank"
              rel="noopener noreferrer"
              buttonClass="text-left p-0"
            >
              <span>{resolvedTo}</span>
            </Link>
          ) : (
            <span className="text-chariot-text-primary text-sm break-all">
              {resolvedTo}
            </span>
          )}
        </div>
      )}
      <LoginPanelRequestCell
        label="Crawled"
        value={formatDate(crawledAt)}
        copy={false}
      />
      <LoginPanelRequestCell label="Method" value={method} />
      <LoginPanelRequestCell label="Source" value={sourceURL} />
      {contentLength && (
        <LoginPanelRequestCell label="Content-Length" value={contentLength} />
      )}
    </div>
  );

  if (detectorName || matchedAtURL) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Detection',
      value: 'login_panel_detection',
    });
    sections['login_panel_detection'] = (
      <div className="text-chariot-text-secondary grid grid-cols-2 gap-2 text-sm">
        {detectorName && (
          <LoginPanelRequestCell label="Detector" value={detectorName} />
        )}
        {matchedAtURL && (
          <div className="col-span-2 flex flex-col gap-1">
            <span className="text-chariot-text-tertiary text-xs">
              Matched At:
            </span>
            {isValidExternalURL(matchedAtURL) ? (
              <Link
                to={matchedAtURL}
                target="_blank"
                rel="noopener noreferrer"
                buttonClass="text-left p-0"
                className="text-xs break-all"
              >
                <span className="text-xs break-all">{matchedAtURL}</span>
              </Link>
            ) : (
              <span className="text-chariot-text-primary text-xs break-all">
                {matchedAtURL}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  if (snippet) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Matched Evidence',
      value: 'login_panel_snippet',
    });
    sections['login_panel_snippet'] = (
      <pre className="text-chariot-text-primary m-0 max-h-96 overflow-auto border-0 bg-transparent p-0 font-mono text-xs whitespace-pre-wrap">
        {snippet}
      </pre>
    );
  }

  if (allInputFields.length > 0) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Discovered Input Fields',
      value: 'login_panel_inputs',
    });
    sections['login_panel_inputs'] = (
      <div className="flex flex-wrap items-center gap-2">
        {allInputFields.map(field => (
          <DashedTag
            copy={true}
            key={field}
            className="px-3 py-0.5 text-xs normal-case"
            label={field}
          />
        ))}
      </div>
    );
  }
  if (screenshot) {
    tempTabs['LOGIN_PANEL'].push({
      label: 'Screenshots',
      value: 'login_panel_screenshots',
    });
    sections['login_panel_screenshots'] = (
      <MarkdownPreview
        source={`![${screenshot?.split('/').pop()}](#file/${screenshot})`}
        thumbnail={true}
      />
    );
  }
}
